import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import Avatar from "./component/Avatar";

const ADMIN_API_BASE_URL = window.location.origin;

function UserTable({ onRowClick }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  const PER = 15;
  const normalizeStatus = (value) => String(value || "Pending").trim().toLowerCase();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setActionError("");
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${ADMIN_API_BASE_URL}/api/students`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || "Failed to load students");
      setUsers(data?.students || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setActionError(error.message || "Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const updateStudent = async (userId, action) => {
    try {
      setActionError("");
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${ADMIN_API_BASE_URL}/api/students/${userId}/${action}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || `Failed to ${action} student`);
      const nextStatus = action === "approve" ? "Approved" : "Rejected";
      setUsers((prev) => prev.map((user) => user.id === userId ? { ...user, status: nextStatus } : user));
      if (action === "approve" && data?.email_warning) {
        setActionError(`Student approved, but approval email could not be sent: ${data.email_warning}`);
      }
    } catch (error) {
      console.error(`${action} failed:`, error);
      setActionError(error.message || `Failed to ${action} student`);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter((user) => !q || `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase().includes(q) || user.email?.toLowerCase().includes(q));
  }, [users, search]);

  const pages = Math.ceil(filtered.length / PER);
  const visible = filtered.slice(page * PER, (page + 1) * PER);

  if (loading) return <div className="bg-white rounded-2xl border border-gray-100 shadow-sm"><div className="p-6 text-center">Loading users...</div></div>;

  return (
    <div className="space-y-4">
      <div className="relative">
        <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm" />
        <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Search by name or email..." className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
      </div>
      {actionError && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700">{actionError}</div>}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-100 bg-gray-50/80">{["Name", "Email", "Status", "Created", "Actions"].map((h) => <th key={h} className="px-4 py-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-50">
              {visible.map((user) => {
                const status = normalizeStatus(user.status);
                return (
                  <tr key={user.id} onClick={() => onRowClick?.(user)} className="hover:bg-orange-50/50 cursor-pointer transition-colors group">
                    <td className="px-4 py-3"><div className="flex items-center gap-3"><Avatar firstName={user.first_name} lastName={user.last_name} /><span className="font-semibold text-gray-800 whitespace-nowrap">{user.first_name} {user.last_name}</span></div></td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{user.email || "—"}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${status === "approved" ? "bg-green-100 text-green-700 border-green-200" : status === "rejected" ? "bg-red-100 text-red-700 border-red-200" : "bg-yellow-100 text-yellow-700 border-yellow-200"}`}>{user.status || "Pending"}</span></td>
                    <td className="px-4 py-3 text-xs text-gray-500">{user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      {status === "pending" && <div className="flex items-center gap-2"><button onClick={() => updateStudent(user.id, "approve")} className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 border border-green-200 rounded-lg hover:bg-green-200 transition">Approve</button><button onClick={() => updateStudent(user.id, "reject")} className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 border border-red-200 rounded-lg hover:bg-red-200 transition">Reject</button></div>}
                      {status === "approved" && <span className="text-xs font-medium text-green-600">Approved</span>}
                      {status === "rejected" && <span className="text-xs font-medium text-red-600">Rejected</span>}
                    </td>
                  </tr>
                );
              })}
              {!visible.length && <tr><td colSpan={5} className="text-center py-8 text-gray-400">No users found</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
          <span>{filtered.length ? `${page * PER + 1}-${Math.min((page + 1) * PER, filtered.length)} of ${filtered.length}` : "0 users"}</span>
          <div className="flex gap-2"><button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30">← Prev</button><span className="px-2 py-1.5">{pages ? page + 1 : 0}/{pages || 0}</span><button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1 || pages === 0} className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30">Next →</button></div>
        </div>
      </div>
    </div>
  );
}

export default UserTable;
